import { db } from "config/firebase";
import { 
  collection, 
  doc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  getDocs, 
  getDoc,
  query, 
  where,
  Timestamp 
} from "firebase/firestore";

/**
 * Compute if promotion is currently active based on dates
 */
export function computeIsActive(startDate, endDate) {
  const now = new Date();
  const start = startDate instanceof Timestamp ? startDate.toDate() : startDate;
  const end = endDate instanceof Timestamp ? endDate.toDate() : endDate;
  return start <= now && end >= now;
}

/**
 * Compute promotion status: 'active', 'scheduled', or 'expired'
 */
export function computeStatus(startDate, endDate) {
  const now = new Date();
  const start = startDate instanceof Timestamp ? startDate.toDate() : new Date(startDate);
  const end = endDate instanceof Timestamp ? endDate.toDate() : new Date(endDate);

  if (start > now) return 'scheduled';
  if (end < now) return 'expired';
  return 'active';
}

/**
 * Create a promotion document in the promotions collection
 * @param {string} sku - Product SKU
 * @param {Object} discount - Discount object with all fields
 * @param {DocumentReference} productRef - Reference to product document
 * @param {string} productName - Product display name
 * @returns {Promise<DocumentReference>} Reference to created promotion
 */
export async function createPromotionDoc(sku, discount, productRef, productName) {
  const promotionsRef = collection(db, 'promotions');
  
  const promotionData = {
    sku,
    productSku: sku, // Denormalized for fast queries
    productRef,
    productName,
    name: discount.name,
    value: discount.value,
    startDate: discount.startDate instanceof Timestamp ? discount.startDate : Timestamp.fromDate(discount.startDate),
    endDate: discount.endDate instanceof Timestamp ? discount.endDate : Timestamp.fromDate(discount.endDate),
    notes: discount.notes || '',
    createdBy: discount.createdBy,
    createdAt: discount.createdAt || Timestamp.now(),
    createdVia: discount.createdVia || 'bulk_upload',
    batchId: discount.batchId,
    isActive: computeIsActive(discount.startDate, discount.endDate)
  };

  return await addDoc(promotionsRef, promotionData);
}

/**
 * Update a promotion document
 * @param {string} promotionId - Document ID
 * @param {Object} updates - Fields to update
 * @returns {Promise<void>}
 */
export async function updatePromotionDoc(promotionId, updates) {
  const promotionRef = doc(db, 'promotions', promotionId);
  
  // Recompute isActive if dates changed
  if (updates.startDate || updates.endDate) {
    const docSnap = await getDoc(promotionRef);
    if (docSnap.exists()) {
      const data = docSnap.data();
      const startDate = updates.startDate || data.startDate;
      const endDate = updates.endDate || data.endDate;
      updates.isActive = computeIsActive(startDate, endDate);
    }
  }

  return await updateDoc(promotionRef, updates);
}

/**
 * Delete a promotion document
 * @param {string} promotionId - Document ID
 * @returns {Promise<void>}
 */
export async function deletePromotionDoc(promotionId) {
  const promotionRef = doc(db, 'promotions', promotionId);
  return await deleteDoc(promotionRef);
}

/**
 * Query all promotions (no filter)
 * @returns {Promise<Array>} Array of promotion objects with IDs
 */
export async function queryAllPromotions() {
  const promotionsRef = collection(db, 'promotions');

  try {
    const snapshot = await getDocs(promotionsRef);

    const promotions = [];
    snapshot.forEach(docSnapshot => {
      try {
        const data = docSnapshot.data();
        // Extract only primitive fields to avoid DocumentReference serialization issues
        promotions.push({
          id: docSnapshot.id,
          sku: data.sku,
          productSku: data.productSku,
          productName: data.productName,
          name: data.name,
          value: data.value,
          startDate: data.startDate,
          endDate: data.endDate,
          notes: data.notes,
          createdBy: data.createdBy,
          createdAt: data.createdAt,
          createdVia: data.createdVia,
          batchId: data.batchId,
          isActive: data.isActive
        });
      } catch (error) {
        console.error(`Error processing promotion document ${docSnapshot.id}:`, error);
      }
    });

    return promotions;
  } catch (error) {
    console.error('Error querying promotions collection:', error);
    return [];
  }
}

/**
 * Query active promotions only
 * @returns {Promise<Array>} Array of active promotion objects
 */
export async function queryActivePromotions() {
  const promotionsRef = collection(db, 'promotions');
  const q = query(promotionsRef, where('isActive', '==', true));
  const snapshot = await getDocs(q);

  const promotions = [];
  snapshot.forEach(docSnapshot => {
    try {
      const data = docSnapshot.data();
      promotions.push({ id: docSnapshot.id, ref: docSnapshot.ref, ...data });
    } catch (error) {
      console.error(`Error processing active promotion ${docSnapshot.id}:`, error);
    }
  });

  return promotions;
}

/**
 * Query promotions by batch ID (for rollback)
 * @param {string} batchId - Batch ID to query
 * @returns {Promise<Array>} Array of promotion objects in batch
 */
export async function queryPromotionsByBatchId(batchId) {
  const promotionsRef = collection(db, 'promotions');
  const q = query(promotionsRef, where('batchId', '==', batchId));
  const snapshot = await getDocs(q);

  const promotions = [];
  snapshot.forEach(docSnapshot => {
    try {
      const data = docSnapshot.data();
      promotions.push({ id: docSnapshot.id, ref: docSnapshot.ref, ...data });
    } catch (error) {
      console.error(`Error processing promotion ${docSnapshot.id} in batch ${batchId}:`, error);
    }
  });

  return promotions;
}

/**
 * Query promotions by SKU
 * @param {string} sku - Product SKU
 * @returns {Promise<Array>} Array of promotions for this SKU
 */
export async function queryPromotionsBySKU(sku) {
  const promotionsRef = collection(db, 'promotions');
  const q = query(promotionsRef, where('productSku', '==', sku));
  const snapshot = await getDocs(q);

  const promotions = [];
  snapshot.forEach(docSnapshot => {
    try {
      const data = docSnapshot.data();
      promotions.push({ id: docSnapshot.id, ref: docSnapshot.ref, ...data });
    } catch (error) {
      console.error(`Error processing promotion ${docSnapshot.id} for SKU ${sku}:`, error);
    }
  });

  return promotions;
}

/**
 * Batch create multiple promotions
 * Used during bulk upload
 * @param {Array} promotionsData - Array of promotion objects
 * @returns {Promise<Array>} Array of created document references
 */
export async function batchCreatePromotions(promotionsData) {
  const refs = [];
  
  for (const promoData of promotionsData) {
    const ref = await createPromotionDoc(
      promoData.sku,
      promoData.discount,
      promoData.productRef,
      promoData.productName
    );
    refs.push(ref);
  }
  
  return refs;
}

/**
 * Batch delete promotions by IDs
 * @param {Array<string>} promotionIds - Array of promotion document IDs
 * @returns {Promise<void>}
 */
export async function batchDeletePromotions(promotionIds) {
  const deletePromises = promotionIds.map(id => deletePromotionDoc(id));
  return await Promise.all(deletePromises);
}
